import { createContext, type ReactNode, useContext, useMemo } from 'react';

// App configuration surfaced from VITE_-prefixed env vars.
// NEVER put secrets here — everything on `import.meta.env.VITE_*` ships to the browser.
export type AppConfig = {
  deploymentEnv: string;
  assessmentApiBase: string;
  supportUrl: string;
  /** True in local/QA — enables reviewer QA-preview affordances (draft editions). */
  isPreviewMode: boolean;
};

const readConfig = (): AppConfig => {
  const deploymentEnv = import.meta.env.VITE_DEPLOYMENT_ENV || 'local';
  return {
    deploymentEnv,
    // Same-origin `/api` by default; overridable per-env for direct QA testing.
    assessmentApiBase: import.meta.env.VITE_ASSESSMENT_API_BASE || '/api',
    supportUrl: import.meta.env.VITE_SUPPORT_URL || 'https://support.nexford.org',
    isPreviewMode: deploymentEnv === 'local' || deploymentEnv === 'qa',
  };
};

const ConfigContext = createContext<AppConfig | null>(null);

type Props = {
  children: ReactNode;
  /** Test/override hook — production reads from env. */
  config?: Partial<AppConfig>;
};

export const ConfigProvider = ({ children, config }: Props) => {
  const value = useMemo<AppConfig>(() => ({ ...readConfig(), ...config }), [config]);
  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>;
};

export const useConfig = (): AppConfig => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
};
